import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updateDatabaseProperties, getIngestFileTypes, getDatabaseIndexes, getConversionApis } from "../../api/adminApi";

const COMPRESS_PRESETS = [
  { id: "None",     label: "None",     level: "None",         chunkSize: 1  },
  { id: "Fastest",  label: "Fastest",  level: "Fastest",      chunkSize: 8  },
  { id: "Optimal",  label: "Optimal",  level: "Optimal",      chunkSize: 16 },
  { id: "Smallest", label: "Smallest", level: "SmallestSize",  chunkSize: 32 },
  { id: "Custom",   label: "Custom",   level: null,           chunkSize: null },
];
const RENDER_FORMATS   = ["PDF","PNG","TIFF","JPG","GIF"].map(id => ({ id, label: id }));
const DOWNLOAD_FORMATS = [
  { id: "ZipArchive",    label: "ZIP Archive" },
  { id: "OriginalFiles", label: "Original Files" },
  { id: "MergedPDF",     label: "Merged PDF" },
];

const SYSTEM_MAPPABLE_COLUMNS = [
  { name: "OriginalFileName", type: "text" },
  { name: "ArchiveNo",        type: "text" },
  { name: "DocumentDate",     type: "date" },
  { name: "Sender",           type: "text" },
  { name: "Channel",          type: "text" },
  { name: "DocumentUUID",     type: "text", autoDefault: "NEWGUID()" },
  { name: "Status",           type: "text", autoDefault: "\"Archived\"" },
];

const DATE_FORMAT_SUGGESTIONS = [
  "yyyyMMdd", "ddMMyyyy", "dd.MM.yyyy", "dd/MM/yyyy", "yyyy-MM-dd", "MM/dd/yyyy",
];

// ── Function definitions ───────────────────────────────────────────────────────

const KNOWN_FUNCTIONS = [
  "UPPER","LOWER","TRIM","SUBSTR","REPLACE","PADLEFT","PADRIGHT","DATEFORMAT","NEWGUID",
];

// noArg: true  → no source-field selector; these wrap the last field token in-place
// noArg: false → param panel shows a source-field picker + optional extra params
const FUNCTION_DEFS = [
  {
    name: "UPPER", label: "Uppercase", shortIcon: "Aa↑",
    desc: "Convert to UPPERCASE",
    noArg: true, paramDefs: [], buildExtra: () => "",
  },
  {
    name: "LOWER", label: "Lowercase", shortIcon: "aa↓",
    desc: "Convert to lowercase",
    noArg: true, paramDefs: [], buildExtra: () => "",
  },
  {
    name: "TRIM", label: "Trim", shortIcon: "⌶",
    desc: "Remove leading & trailing whitespace",
    noArg: true, paramDefs: [], buildExtra: () => "",
  },
  {
    name: "DATEFORMAT", label: "Date Format", shortIcon: "📅",
    desc: "Format a date field into a string",
    noArg: false,
    paramDefs: [
      { key: "format", label: "Format", type: "text", placeholder: "yyyyMMdd", defaultVal: "yyyyMMdd" },
    ],
    buildExtra: (p) => `, ${p.format ?? "yyyyMMdd"}`,
    previewTpl: (p, arg) => `DATEFORMAT(${arg || "…"}, ${p.format ?? "yyyyMMdd"})`,
  },
  {
    name: "SUBSTR", label: "Substring", shortIcon: "[ ]",
    desc: "Extract a portion of text",
    noArg: false,
    paramDefs: [
      { key: "start",  label: "Start",  type: "number", placeholder: "0",  defaultVal: "0"  },
      { key: "length", label: "Length", type: "number", placeholder: "10", defaultVal: "10" },
    ],
    buildExtra: (p) => `, ${p.start ?? "0"}, ${p.length ?? "10"}`,
    previewTpl: (p, arg) => `SUBSTR(${arg || "…"}, ${p.start ?? "0"}, ${p.length ?? "10"})`,
  },
  {
    name: "REPLACE", label: "Replace", shortIcon: "⇄",
    desc: "Find and replace text",
    noArg: false,
    paramDefs: [
      { key: "from", label: "Find",         type: "text", placeholder: "old…", defaultVal: "" },
      { key: "to",   label: "Replace with", type: "text", placeholder: "new…", defaultVal: "" },
    ],
    buildExtra: (p) => `, "${p.from ?? ""}", "${p.to ?? ""}"`,
    previewTpl: (p, arg) => `REPLACE(${arg || "…"}, "${p.from ?? ""}", "${p.to ?? ""}")`,
  },
  {
    name: "PADLEFT", label: "Pad Left", shortIcon: "⇤",
    desc: "Left-pad to a fixed length",
    noArg: false,
    paramDefs: [
      { key: "length", label: "Length",   type: "number", placeholder: "10", defaultVal: "10" },
      { key: "char",   label: "Pad char", type: "text",   placeholder: "0",  defaultVal: "0"  },
    ],
    buildExtra: (p) => `, ${p.length ?? "10"}, "${p.char ?? "0"}"`,
    previewTpl: (p, arg) => `PADLEFT(${arg || "…"}, ${p.length ?? "10"}, "${p.char ?? "0"}")`,
  },
  {
    name: "PADRIGHT", label: "Pad Right", shortIcon: "⇥",
    desc: "Right-pad to a fixed length",
    noArg: false,
    paramDefs: [
      { key: "length", label: "Length",   type: "number", placeholder: "10", defaultVal: "10" },
      { key: "char",   label: "Pad char", type: "text",   placeholder: " ",  defaultVal: " "  },
    ],
    buildExtra: (p) => `, ${p.length ?? "10"}, "${p.char ?? " "}"`,
    previewTpl: (p, arg) => `PADRIGHT(${arg || "…"}, ${p.length ?? "10"}, "${p.char ?? " "}")`,
  },
  {
    name: "NEWGUID", label: "New GUID", shortIcon: "⊛",
    desc: "Generate a new unique identifier (GUID / UUID) at processing time",
    noArg: true, noField: true, paramDefs: [], buildExtra: () => "",
  },
];

// ── Expression helpers ────────────────────────────────────────────────────────

function parseExpression(str) {
  if (!str) return [];
  const segs = str.split(/(\{[^}]*\})/g).filter(Boolean);
  const out  = [];
  for (const seg of segs) {
    if (/^\{[^}]*\}$/.test(seg)) {
      out.push({ type: "field", value: seg.slice(1, -1) });
    } else {
      let pos = 0;
      const re = new RegExp(`(${KNOWN_FUNCTIONS.join("|")})\\(`, "g");
      let m;
      while ((m = re.exec(seg)) !== null) {
        if (m.index > pos) splitByClose(seg.slice(pos, m.index), out);
        out.push({ type: "func_open", value: m[1] });
        pos = m.index + m[0].length;
      }
      if (pos < seg.length) splitByClose(seg.slice(pos), out);
    }
  }
  return out;
}

function splitByClose(s, out) {
  const chunks = s.split(")");
  for (let i = 0; i < chunks.length; i++) {
    if (chunks[i]) out.push({ type: "literal", value: chunks[i] });
    if (i < chunks.length - 1) out.push({ type: "func_close" });
  }
}

function buildExpression(parts) {
  return parts.map(p =>
    p.type === "field"      ? `{${p.value}}`  :
    p.type === "func_open"  ? `${p.value}(`   :
    p.type === "func_close" ? `)`             :
    p.value
  ).join("");
}

function findMatchingClose(parts, openIdx) {
  let depth = 1;
  for (let i = openIdx + 1; i < parts.length; i++) {
    if (parts[i].type === "func_open")  depth++;
    if (parts[i].type === "func_close") { depth--; if (!depth) return i; }
  }
  return -1;
}

// ── Expression evaluator (sample data preview) ────────────────────────────────

const SYSTEM_SAMPLE_DATA = {
  OriginalFileName: "document_0042.afp",
  ArchiveNo:        "ARC00001",
  DocumentDate:     "20240315",
  Sender:           "JOHN SMITH",
  Channel:          "EMAIL",
};

function buildSampleData(dbFields) {
  const data = { ...SYSTEM_SAMPLE_DATA };
  for (const ix of dbFields) {
    const name  = ix.fieldName || ix.FieldName || "";
    const dtype = (ix.dataType || ix.DataType || "text").toLowerCase();
    if (!name) continue;
    if (dtype === "date" || dtype === "datetime") data[name] = "20240315";
    else if (dtype === "number" || dtype === "int" || dtype === "integer") data[name] = "12345";
    else data[name] = name.slice(0, 8).toUpperCase();
  }
  return data;
}

function splitArgs(argsStr) {
  const args = [];
  let cur = "", inQuote = false, qChar = "";
  for (const ch of argsStr) {
    if (!inQuote && (ch === '"' || ch === "'")) { inQuote = true; qChar = ch; cur += ch; }
    else if (inQuote && ch === qChar)            { inQuote = false; cur += ch; }
    else if (!inQuote && ch === ",")             { args.push(cur.trim()); cur = ""; }
    else                                          { cur += ch; }
  }
  if (cur.trim()) args.push(cur.trim());
  return args;
}

// Resolve a bare identifier against sampleData if it matches a field name
function resolveArg(arg, sampleData) {
  if (!sampleData) return arg;
  const trimmed = arg.trim();
  // Bare identifier (no quotes, no spaces, no braces) → try as field reference
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed) && trimmed in sampleData)
    return String(sampleData[trimmed] ?? "");
  return arg;
}

// Normalise Java/moment.js format tokens to C#/standard equivalents
function normalizeDateFmt(fmt) {
  return fmt.replace(/YYYY/g, "yyyy").replace(/YY/g, "yy").replace(/DD/g, "dd");
}

// Parse a date string using common d/M/yyyy and yyyy-MM-dd patterns
function parseDateAuto(str) {
  if (!str) return null;
  const patterns = [
    // d/M/yyyy — Turkish / European short form  e.g. "14/1/2037"
    { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, fn: (m) => new Date(+m[3], +m[2]-1, +m[1]) },
    // MM/dd/yyyy
    { re: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/,  fn: (m) => new Date(2000 + +m[3], +m[2]-1, +m[1]) },
    // yyyy-MM-dd
    { re: /^(\d{4})-(\d{2})-(\d{2})$/,         fn: (m) => new Date(+m[1], +m[2]-1, +m[3]) },
    // yyyyMMdd
    { re: /^(\d{4})(\d{2})(\d{2})$/,           fn: (m) => new Date(+m[1], +m[2]-1, +m[3]) },
  ];
  for (const { re, fn } of patterns) {
    const m = str.match(re);
    if (m) { const d = fn(m); if (!isNaN(d)) return d; }
  }
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

// Format a Date using a subset of C#-style format specifiers
function formatDate(dt, fmt) {
  const pad = (n, w=2) => String(n).padStart(w, "0");
  return fmt
    .replace("yyyy", dt.getFullYear())
    .replace("yy",   pad(dt.getFullYear() % 100))
    .replace("MM",   pad(dt.getMonth() + 1))
    .replace("M",    dt.getMonth() + 1)
    .replace("dd",   pad(dt.getDate()))
    .replace("d",    dt.getDate());
}

function applyFunc(fn, argsStr, sampleData) {
  const args = splitArgs(argsStr).map(a => resolveArg(a, sampleData));
  const val  = args[0] ?? "";
  const strip = s => String(s).replace(/^["']|["']$/g, "");
  switch (fn) {
    case "UPPER":    return val.toUpperCase();
    case "LOWER":    return val.toLowerCase();
    case "TRIM":     return val.trim();
    case "SUBSTR":   { const s = parseInt(args[1]) || 0, l = parseInt(args[2]); return l ? val.substring(s, s + l) : val.substring(s); }
    case "REPLACE":  return val.split(strip(args[1] ?? "")).join(strip(args[2] ?? ""));
    case "PADLEFT":  { const l = parseInt(args[1]) || 0, c = strip(args[2] ?? "") || "0"; return val.padStart(l, c); }
    case "PADRIGHT": { const l = parseInt(args[1]) || 0, c = strip(args[2] ?? "") || " "; return val.padEnd(l, c); }
    case "DATEFORMAT": {
      // 2-arg: DATEFORMAT(value, outputFmt)
      // 3-arg: DATEFORMAT(value, inputFmt, outputFmt)  — inputFmt ignored in preview
      const outputFmt = normalizeDateFmt(strip(args.length >= 3 ? (args[2] ?? "") : (args[1] ?? "yyyy-MM-dd")));
      const dt = parseDateAuto(val);
      return dt ? formatDate(dt, outputFmt) : val;
    }
    case "NEWGUID":  return "a3f8c2d1-4e67-4b90-8f3a-1c2d3e4f5a6b";
    default: return `${fn}(${argsStr})`;
  }
}

function evaluateExpression(expr, sampleData) {
  if (!expr) return "";
  // Substitute {FieldName} references
  let s = expr.replace(/\{([^}]+)\}/g, (_, n) => sampleData[n] ?? n);
  // Iteratively resolve innermost function calls (pass sampleData for bare-name resolution)
  for (let i = 0; i < 20; i++) {
    const prev = s;
    s = s.replace(/([A-Z][A-Z0-9_]*)\(([^()]*)\)/g, (_, fn, inner) => applyFunc(fn, inner, sampleData));
    if (s === prev) break;
  }
  return s;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const DEFAULT_PROPS = {
  documentMode: "BulkContainer",
  acceptedFileTypes: [],
  driverFileExtension: "",
  compressionEnabled: false,
  compressionLevel: "Optimal",
  chunkSize: 16,
  allowedRenderFormats: [],
  bulkDownloadEnabled: true,
  bulkDownloadFormat: "ZipArchive",
  postProcessingFlowId: null,
  keepOriginalInputFile: true,
  keepOriginalInputIndexFile: true,
  columnMappings: {},
};

function parseProps(json) {
  if (!json) return { ...DEFAULT_PROPS };
  try { return { ...DEFAULT_PROPS, ...JSON.parse(json) }; }
  catch { return { ...DEFAULT_PROPS }; }
}

function Toggle({ on, onToggle, label }) {
  return (
    <label className="dbPropsToggle" onClick={onToggle}>
      <span className={`dbPropsToggleTrack${on ? " on" : ""}`}>
        <span className="dbPropsToggleThumb" />
      </span>
      {label && <span>{label}</span>}
    </label>
  );
}

function CheckChip({ checked, label, onChange }) {
  return (
    <label className={`dbPropsCheckChip${checked ? " checked" : ""}`}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: "none" }} />
      {label}
    </label>
  );
}

function getPresetLabel(props) {
  if (!props.compressionEnabled) return "None";
  const match = COMPRESS_PRESETS.find(cp => cp.level === props.compressionLevel && cp.chunkSize === props.chunkSize);
  if (match && match.id !== "None") return `${match.label} ×${match.chunkSize}`;
  return `Custom ×${props.chunkSize}`;
}

function SummaryBadges({ props }) {
  const { t } = useTranslation();
  const mode  = props.documentMode === "AdHoc" ? t("dbProps.adHocShort") : t("dbProps.bulkContainer");
  const types = (props.acceptedFileTypes || []).slice(0, 3).join(", ");
  const more  = (props.acceptedFileTypes || []).length > 3 ? ` +${props.acceptedFileTypes.length - 3}` : "";
  const maps  = Object.values(props.columnMappings || {}).filter(m => m?.expression).length;
  return (
    <>
      <span className="dbPropsBadge">{mode}</span>
      {types && <span className="dbPropsBadge dbPropsBadge--orange">{types}{more}</span>}
      {props.documentMode === "BulkContainer" && <span className="dbPropsBadge">{getPresetLabel(props)}</span>}
      {props.driverFileExtension && <span className="dbPropsBadge dbPropsBadge--orange">.{props.driverFileExtension}</span>}
      {(props.allowedRenderFormats || []).length > 0 && (
        <span className="dbPropsBadge">{t("dbProps.renderPrefix")}{props.allowedRenderFormats.join(", ")}</span>
      )}
      {props.bulkDownloadEnabled && (
        <span className="dbPropsBadge dbPropsBadge--green">
          {DOWNLOAD_FORMATS.find(f => f.id === props.bulkDownloadFormat)?.label ?? props.bulkDownloadFormat}
        </span>
      )}
      {maps > 0 && <span className="dbPropsBadge">{maps} map{maps !== 1 ? "s" : ""}</span>}
    </>
  );
}

// ── Expression Preview (read-only, shown in the mapping table row) ─────────────

function ExpressionPreview({ expression }) {
  const { t } = useTranslation();
  if (!expression) {
    return <span className="dbMappingExprPlaceholder">{t("dbProps.notConfigured")}</span>;
  }
  const parts = parseExpression(expression);
  return (
    <span className="dbMappingExprPreviewInline">
      {parts.map((p, i) => {
        if (p.type === "func_open")  return <span key={i} className="dbMappingPreviewFunc">ƒ {p.value}(</span>;
        if (p.type === "func_close") return <span key={i} className="dbMappingPreviewFuncClose">)</span>;
        if (p.type === "field")      return <span key={i} className="dbMappingPreviewToken">{p.value}</span>;
        return <span key={i} className="dbMappingPreviewLiteral">{p.value}</span>;
      })}
    </span>
  );
}

// ── Expression Builder ────────────────────────────────────────────────────────

const ExpressionBuilder = React.forwardRef(function ExpressionBuilder(
  { expression, onChange },
  ref
) {
  const { t } = useTranslation();
  const [parts, setParts] = useState(() => parseExpression(expression));
  const appendRef         = useRef(null);
  const lastBuilt         = useRef(expression ?? "");
  const partsRef          = useRef(parts);

  React.useImperativeHandle(ref, () => ({
    // Insert a bare field token at the end
    addFieldToken(fieldName) {
      const pending = appendRef.current?.value || "";
      if (appendRef.current) appendRef.current.value = "";
      const cur = partsRef.current;
      const toInsert = [];
      if (pending) toInsert.push({ type: "literal", value: pending });
      toInsert.push({ type: "field", value: fieldName });
      commit([...cur, ...toInsert]);
      setTimeout(() => appendRef.current?.focus(), 0);
    },

    // Insert a complete function call at the end: FUNCNAME({argField}[, extraParams])
    // argField may be "" for literal-arg cases
    insertFunctionCall(funcName, argField, extraParams) {
      const pending = appendRef.current?.value || "";
      if (appendRef.current) appendRef.current.value = "";
      const cur = partsRef.current;
      const flush = pending ? [{ type: "literal", value: pending }] : [];
      const inner = argField
        ? [{ type: "field", value: argField }]
        : [];
      const extra = extraParams ? [{ type: "literal", value: extraParams }] : [];
      const next = [
        ...cur,
        ...flush,
        { type: "func_open", value: funcName },
        ...inner,
        ...extra,
        { type: "func_close" },
      ];
      commit(next);
      setTimeout(() => appendRef.current?.focus(), 0);
    },

    // Wrap the last field token in-place: {FIELD} → FUNCNAME({FIELD})
    // Used for no-arg functions (UPPER, LOWER, TRIM) applied to the most recent field
    wrapLastField(funcName) {
      const cur = partsRef.current;
      const lastFieldIdx = [...cur].reverse().findIndex(p => p.type === "field");
      if (lastFieldIdx < 0) return false; // nothing to wrap
      const idx = cur.length - 1 - lastFieldIdx;
      const next = [
        ...cur.slice(0, idx),
        { type: "func_open", value: funcName },
        cur[idx],
        { type: "func_close" },
        ...cur.slice(idx + 1),
      ];
      commit(next);
      return true;
    },
  }));

  useEffect(() => {
    if (expression !== lastBuilt.current) {
      lastBuilt.current = expression ?? "";
      const p = parseExpression(expression);
      partsRef.current = p;
      setParts(p);
    }
  }, [expression]);

  function commit(newParts) {
    const str = buildExpression(newParts);
    lastBuilt.current = str;
    partsRef.current  = newParts;
    setParts(newParts);
    onChange(str);
  }

  function removePart(i) {
    const cur = partsRef.current;
    let next;
    if (cur[i]?.type === "func_open") {
      const closeIdx = findMatchingClose(cur, i);
      next = cur.filter((_, idx) => idx !== i && (closeIdx < 0 || idx !== closeIdx));
    } else {
      next = cur.filter((_, idx) => idx !== i);
    }
    commit(next);
  }

  function updateLiteral(i, value) {
    const cur  = partsRef.current;
    const next = [...cur];
    if (value === "") next.splice(i, 1);
    else next[i] = { type: "literal", value };
    commit(next);
  }

  function flushAppend() {
    const val = appendRef.current?.value || "";
    if (!val) return;
    if (appendRef.current) appendRef.current.value = "";
    commit([...partsRef.current, { type: "literal", value: val }]);
  }

  const hasContent = parts.length > 0;

  return (
    <div
      className={`dbExprBuilder${hasContent ? " hasContent" : ""}`}
      onClick={() => { if (!hasContent) appendRef.current?.focus(); }}
    >
      {parts.map((part, i) => {
        if (part.type === "func_open") {
          return (
            <span key={`${i}fo`} className="dbExprFuncToken">
              <span className="dbExprFuncIcon">ƒ</span>
              <span className="dbExprFuncName">{part.value}</span>
              <span className="dbExprFuncParen">(</span>
              <button type="button" className="dbExprTokenX"
                onMouseDown={e => { e.preventDefault(); removePart(i); }}
                tabIndex={-1} title={`Remove ${part.value}() call`}>×</button>
            </span>
          );
        }
        if (part.type === "func_close") {
          return <span key={`${i}fc`} className="dbExprFuncClose">)</span>;
        }
        if (part.type === "field") {
          return (
            <span key={`${i}f`} className="dbExprToken">
              <span className="dbExprTokenLabel">{part.value}</span>
              <button type="button" className="dbExprTokenX"
                onMouseDown={e => { e.preventDefault(); removePart(i); }}
                tabIndex={-1}>×</button>
            </span>
          );
        }
        return (
          <input key={`${i}l`} type="text" className="dbExprLiteralInput"
            value={part.value} size={Math.max(1, part.value.length)}
            onChange={e => updateLiteral(i, e.target.value)} spellCheck={false} />
        );
      })}
      <input ref={appendRef} type="text" className="dbExprAppendInput"
        placeholder={!hasContent ? t("dbProps.expr.builderPlaceholder") : ""}
        onBlur={flushAppend}
        onKeyDown={e => {
          if (e.key === "Enter") { e.preventDefault(); flushAppend(); }
          if (e.key === "Backspace" && !appendRef.current?.value && partsRef.current.length > 0) {
            e.preventDefault();
            removePart(partsRef.current.length - 1);
          }
        }}
        spellCheck={false}
      />
    </div>
  );
});

// ── Expression Editor Modal ───────────────────────────────────────────────────

function ExpressionModal({ colKey, colName, colType, isSystem, mapping, dbFields, onSave, onClose }) {
  const { t } = useTranslation();
  const [expr, setExpr]             = useState(mapping?.expression || "");
  const [dateFormat, setDateFormat]   = useState(mapping?.dateFormat || "");
  const [activeFuncDef, setActiveFuncDef] = useState(null);
  const [funcArg, setFuncArg]         = useState("");   // selected source field for the function
  const [funcParams, setFuncParams]   = useState({});
  const builderRef = useRef(null);

  const isDate = colType === "date" || colType === "datetime";

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function insertField(name) {
    builderRef.current?.addFieldToken(name);
  }

  function handleFunctionClick(fn) {
    if (fn.noField) {
      // No-argument, no-field function — insert directly (e.g. NEWGUID())
      builderRef.current?.insertFunctionCall(fn.name, "", "");
      return;
    }
    if (fn.noArg) {
      // Try to wrap the most recent field token in-place first
      const wrapped = builderRef.current?.wrapLastField(fn.name);
      if (!wrapped) {
        // No field at end: open the param panel so user can pick a source field
        setFuncArg("");
        setFuncParams({});
        setActiveFuncDef(prev => prev?.name === fn.name ? null : fn);
      }
      return;
    }
    // Parameterised function: always open param panel
    const defaults = {};
    fn.paramDefs.forEach(p => { defaults[p.key] = p.defaultVal; });
    setFuncParams(defaults);
    setFuncArg("");
    setActiveFuncDef(prev => prev?.name === fn.name ? null : fn);
  }

  function applyFunctionCall() {
    if (!activeFuncDef) return;
    const extra = activeFuncDef.buildExtra(funcParams);
    builderRef.current?.insertFunctionCall(activeFuncDef.name, funcArg, extra);
    setActiveFuncDef(null);
    setFuncArg("");
    setFuncParams({});
  }

  const paramPreview = activeFuncDef?.previewTpl
    ? activeFuncDef.previewTpl(funcParams, funcArg || "…")
    : activeFuncDef
      ? `${activeFuncDef.name}(${funcArg || "…"})`
      : "";

  const canApply = !!funcArg || dbFields.length === 0;

  function handleSave() {
    const hasContent = expr || dateFormat;
    onSave(hasContent ? { expression: expr, dateFormat } : { expression: "", dateFormat: "" });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="dbExprModal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dbExprModalHeader">
          <span className={`dbMappingColBadge dbMappingColBadge--${isSystem ? "sys" : "idx"}`}>
            {isSystem ? "sys" : "idx"}
          </span>
          <span className="dbExprModalTitle">{colName}</span>
          <div style={{ flex: 1 }} />
          <button className="drawer-close-btn" onClick={onClose} aria-label="Close">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="dbExprModalBody">

          {/* ── Source fields ── */}
          {dbFields.length > 0 && (
            <div className="dbExprModalSection">
              <div className="dbExprModalSectionLabel">{t("dbProps.expr.sourceFields")}</div>
              <div className="dbMappingSourceChips">
                {dbFields.map(ix => {
                  const name = ix.fieldName || ix.FieldName || "";
                  if (!name) return null;
                  return (
                    <button key={name} type="button"
                      className={`dbMappingSourceChip${funcArg === name ? " selected" : ""}`}
                      onMouseDown={e => { e.preventDefault(); }}
                      onClick={() => {
                        if (activeFuncDef) {
                          // If a function panel is open, set as its argument
                          setFuncArg(name);
                        } else {
                          // Otherwise insert bare field token
                          insertField(name);
                        }
                      }}
                      title={activeFuncDef ? `Use ${name} as argument` : `Insert {${name}}`}>
                      {name}
                    </button>
                  );
                })}
              </div>
              {activeFuncDef && (
                <div className="dbExprModalFieldHint">
                  {t("dbProps.expr.clickFieldHint", { name: activeFuncDef.name })}
                </div>
              )}
            </div>
          )}

          {/* ── Functions ── */}
          <div className="dbExprModalSection">
            <div className="dbExprModalSectionLabel">{t("dbProps.expr.functions")}</div>
            <div className="dbMappingFuncChips">
              {FUNCTION_DEFS.map(fn => (
                <button key={fn.name} type="button"
                  className={`dbMappingFuncChip${activeFuncDef?.name === fn.name ? " active" : ""}`}
                  onMouseDown={e => { e.preventDefault(); handleFunctionClick(fn); }}
                  title={fn.desc}>
                  <span className="dbMappingFuncChipIcon">{fn.shortIcon}</span>
                  <span className="dbMappingFuncChipLabel">{fn.label}</span>
                </button>
              ))}
            </div>

            {/* Param panel */}
            {activeFuncDef && (
              <div className="dbMappingFuncParamPanel">
                <div className="dbMappingFuncParamHeader">
                  <span className="dbMappingFuncParamFnName">ƒ {activeFuncDef.name}</span>
                  <span className="dbMappingFuncParamDivider">—</span>
                  <span className="dbMappingFuncParamDesc">{activeFuncDef.desc}</span>
                  {paramPreview && <span className="dbMappingFuncParamPreview">{paramPreview}</span>}
                </div>
                <div className="dbMappingFuncParamBody">
                  {/* Extra params (start/length/format/etc.) */}
                  {activeFuncDef.paramDefs.map(pd => (
                    <label key={pd.key} className="dbMappingFuncParamField">
                      <span className="dbMappingFuncParamLabel">{pd.label}</span>
                      <input type={pd.type} className="dbMappingFuncParamInput"
                        value={funcParams[pd.key] ?? pd.defaultVal}
                        placeholder={pd.placeholder}
                        onChange={e => setFuncParams(prev => ({ ...prev, [pd.key]: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter" && canApply) applyFunctionCall(); }}
                      />
                    </label>
                  ))}
                  <div className="dbMappingFuncParamActions">
                    <button type="button" className="dbMappingFuncParamApply"
                      disabled={!canApply}
                      onMouseDown={e => { e.preventDefault(); applyFunctionCall(); }}>
                      {t("dbProps.expr.insert")}
                    </button>
                    <button type="button" className="dbMappingFuncParamCancel"
                      onMouseDown={e => { e.preventDefault(); setActiveFuncDef(null); setFuncArg(""); setFuncParams({}); }}>
                      {t("common.cancel")}
                    </button>
                    {dbFields.length > 0 && !funcArg && (
                      <span className="dbExprModalFieldHint" style={{ marginLeft: 4 }}>
                        {t("dbProps.expr.selectFieldFirst")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Expression builder ── */}
          <div className="dbExprModalSection dbExprModalBuilderSection">
            <div className="dbExprModalSectionLabel">{t("dbProps.expr.expression")}</div>
            <ExpressionBuilder ref={builderRef} expression={expr} onChange={setExpr} />
          </div>

          {/* ── Date format (date columns) ── */}
          {isDate && expr && (
            <div className="dbExprModalSection">
              <div className="dbExprModalSectionLabel">{t("dbProps.expr.dateFormat")}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="text" className="dbMappingFmtInput" style={{ width: 160 }}
                  placeholder="e.g. yyyyMMdd"
                  value={dateFormat}
                  onChange={e => setDateFormat(e.target.value)}
                  list="dbExprModalDateFmtList" />
                <datalist id="dbExprModalDateFmtList">
                  {DATE_FORMAT_SUGGESTIONS.map(f => <option key={f} value={f} />)}
                </datalist>
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{t("dbProps.expr.datePatternHint")}</span>
              </div>
            </div>
          )}

          {/* ── Live expression preview ── */}
          {expr && (
            <div className="dbExprModalPreview">
              <span className="dbExprModalPreviewLabel">{t("dbProps.expr.resultExpr")}</span>
              <code className="dbExprModalPreviewCode">{expr}</code>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="dbExprModalFooter">
          {expr && (
            <button type="button" className="dbExprModalClearBtn"
              onClick={() => { setExpr(""); setDateFormat(""); }}>
              {t("common.clear")}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" className="secondary-btn" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className="primary-btn" onClick={handleSave}>{t("dbProps.expr.ok")}</button>
        </div>
      </div>
    </div>
  );
}

// ── Mapping table row ─────────────────────────────────────────────────────────

function MappingTableRow({ colKey, colName, colType, isSystem, autoDefault, mapping, onMappingChange, onEdit, sampleData }) {
  const { t } = useTranslation();
  const expression   = mapping?.expression || "";
  const isConfigured = !!expression;
  const preview      = isConfigured ? evaluateExpression(expression, sampleData) : "";

  return (
    <tr className={`dbMappingRow${isConfigured ? " dbMappingRow--active" : ""}`}>
      <td className="dbMappingColCell">
        <span className={`dbMappingColBadge dbMappingColBadge--${isSystem ? "sys" : "idx"}`}>
          {isSystem ? "sys" : "idx"}
        </span>
        <span className="dbMappingColName">{colName}</span>
      </td>
      <td className="dbMappingExprCell">
        <div className="dbMappingExprRow">
          {isConfigured
            ? <ExpressionPreview expression={expression} />
            : autoDefault
              ? <span className="dbMappingAutoDefault" title={t("dbProps.autoPopulatedTitle")}>
                  <span className="dbMappingAutoDefaultIcon">⚙</span>
                  {autoDefault}
                </span>
              : <span className="dbMappingExprPlaceholder">{t("dbProps.notConfigured")}</span>
          }
          <button type="button" className="dbMappingEditBtn" onClick={onEdit} title={t("dbProps.editExprTitle")}>
            ···
          </button>
        </div>
      </td>
      <td className="dbMappingPreviewCell">
        {isConfigured && preview && (
          <code className="dbMappingExprCode" title={t("dbProps.mappingSampleTitle")}>{preview}</code>
        )}
      </td>
      <td className="dbMappingActionCell">
        {isConfigured && (
          <button type="button" className="dbMappingClearBtn"
            onClick={() => onMappingChange({ expression: "", dateFormat: "" })} title={t("common.clear")}>×</button>
        )}
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function derivePreset(p) {
  if (!p.compressionEnabled) return "None";
  const match = COMPRESS_PRESETS.find(cp => cp.level === p.compressionLevel && cp.chunkSize === p.chunkSize);
  return match ? match.id : "Custom";
}

export default function DatabasePropertiesPanel({ database, token, open, onToggle, onPropertiesSaved, section = "all" }) {
  const { t } = useTranslation();
  const [props, setProps]       = useState(() => {
    const p = parseProps(database?.propertiesJson);
    if (!p.driverFileExtension && database?.indexFileExtension) p.driverFileExtension = database.indexFileExtension;
    return p;
  });
  const [activePreset, setActivePreset] = useState(() => derivePreset(parseProps(database?.propertiesJson)));
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState("");
  const [saveErr, setSaveErr]   = useState("");
  const saveMsgTimer            = useRef(null);
  const [editingCol, setEditingCol] = useState(null);

  // Sync props state when propertiesJson changes externally (e.g. after an import,
  // or after our own save round-trip via onPropertiesSaved).
  useEffect(() => {
    const p = parseProps(database?.propertiesJson);
    if (!p.driverFileExtension && database?.indexFileExtension) p.driverFileExtension = database.indexFileExtension;
    setProps(p);
    setActivePreset(derivePreset(p));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [database?.propertiesJson, database?.indexFileExtension]);

  // Reset ephemeral UI state only when switching to a different database.
  // Must NOT depend on propertiesJson — that would wipe the "✓ Saved" message
  // the instant the parent echoes the new propertiesJson back down after a save,
  // making it look like the first click did nothing.
  useEffect(() => {
    setSaveMsg(""); setSaveErr("");
    setEditingCol(null);
  }, [database?.id]);

  const [dbFields, setDbFields] = useState([]);
  useEffect(() => {
    if (!token || !database?.id) { setDbFields([]); return; }
    getDatabaseIndexes(token, database.id)
      .then(res => setDbFields(res?.indexes || res || []))
      .catch(() => setDbFields([]));
  }, [token, database?.id]);

  const [allFileTypes, setAllFileTypes] = useState([]);
  useEffect(() => {
    if (!token) return;
    getIngestFileTypes(token)
      .then(data => setAllFileTypes(Array.isArray(data) ? data.filter(ft => ft.isActive) : []))
      .catch(() => {});
  }, [token]);

  const [conversionApis, setConversionApis] = useState([]);
  useEffect(() => {
    if (!token) return;
    getConversionApis(token)
      .then(data => setConversionApis(Array.isArray(data) ? data.filter(ca => ca.isActive !== false) : []))
      .catch(() => {});
  }, [token]);

  const [conversionWarn, setConversionWarn] = useState(null); // { missing: [{input, output}] }
  const navigate = useNavigate();

  function set(key, value) { setProps(prev => ({ ...prev, [key]: value })); }
  function setMany(patch)  { setProps(prev => ({ ...prev, ...patch })); }

  function updateColumnMapping(colName, mapping) {
    const current = props.columnMappings || {};
    const hasContent = mapping.expression || mapping.dateFormat;
    const updated = hasContent
      ? { ...current, [colName]: mapping }
      : Object.fromEntries(Object.entries(current).filter(([k]) => k !== colName));
    set("columnMappings", updated);
  }

  function applyCompressionPreset(presetId) {
    setActivePreset(presetId);
    if (presetId === "None")   setMany({ compressionEnabled: false, compressionLevel: "None", chunkSize: 1 });
    else if (presetId === "Custom") setMany({ compressionEnabled: true });
    else {
      const preset = COMPRESS_PRESETS.find(cp => cp.id === presetId);
      if (preset) setMany({ compressionEnabled: true, compressionLevel: preset.level, chunkSize: preset.chunkSize });
    }
  }

  function hasConversionApi(inputId, outputId) {
    // BULKPDF is natively PDF — no conversion needed
    if (inputId.toUpperCase() === "BULKPDF" && outputId.toUpperCase() === "PDF") return true;
    return conversionApis.some(
      ca => ca.inputTypeId?.toUpperCase() === inputId.toUpperCase() &&
            ca.outputTypeId?.toUpperCase() === outputId.toUpperCase()
    );
  }

  function toggleFileType(id) {
    const cur = props.acceptedFileTypes || [];
    const isAdding = !cur.includes(id);
    if (isAdding) {
      const outputs = props.allowedRenderFormats || [];
      const missing = outputs
        .filter(outId => !hasConversionApi(id, outId))
        .map(outId => ({ input: id, output: outId }));
      if (missing.length > 0) {
        setConversionWarn({ missing });
        return;
      }
    }
    setProps(prev => {
      const c = prev.acceptedFileTypes || [];
      return { ...prev, acceptedFileTypes: c.includes(id) ? c.filter(x => x !== id) : [...c, id] };
    });
  }

  function toggleRenderFormat(id) {
    const cur = props.allowedRenderFormats || [];
    const isAdding = !cur.includes(id);
    if (isAdding) {
      const inputs = props.acceptedFileTypes || [];
      const missing = inputs
        .filter(inId => !hasConversionApi(inId, id))
        .map(inId => ({ input: inId, output: id }));
      if (missing.length > 0) {
        setConversionWarn({ missing });
        return;
      }
    }
    setProps(prev => {
      const c = prev.allowedRenderFormats || [];
      return { ...prev, allowedRenderFormats: c.includes(id) ? c.filter(x => x !== id) : [...c, id] };
    });
  }

  async function handleSave() {
    setSaving(true); setSaveMsg(""); setSaveErr("");
    try {
      await updateDatabaseProperties(token, database.id, props);
      setSaveMsg(t("dbProps.saved"));
      if (saveMsgTimer.current) clearTimeout(saveMsgTimer.current);
      saveMsgTimer.current = setTimeout(() => setSaveMsg(""), 3000);
      if (onPropertiesSaved) onPropertiesSaved(JSON.stringify(props));
    } catch (e) {
      setSaveErr(e?.message || t("dbProps.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  const tabMode = section !== "all";
  const isBulk = props.documentMode === "BulkContainer";
  const sampleData = React.useMemo(() => buildSampleData(dbFields), [dbFields]);
  const fileTypeOptions = allFileTypes
    .filter(ft => ft.mode === "Both" || ft.mode === (isBulk ? "BulkContainer" : "AdHoc"))
    .map(ft => ({ id: ft.typeId, label: ft.label }));
  const columnMappings = props.columnMappings || {};

  return (
    <div className={`dbPropsPanel${tabMode ? " dbPropsPanel--tab" : ""}`}>
      {!tabMode && (
        <div className="dbPropsPanelHead" onClick={onToggle} role="button" aria-expanded={open}>
          <span className="dbPropsPanelTitle">{t("dbProps.title")}</span>
          <div className="dbPropsPanelBadges">{!open && <SummaryBadges props={props} />}</div>
          <span className={`dbPropsChevron${open ? " open" : ""}`}>▾</span>
        </div>
      )}

      {(tabMode || open) && (
        <div className="dbPropsPanelBody">

          {(section === "all" || section === "properties") && (<>
          <div className="dbPropsSection">
            <div className="dbPropsSectionTitle">{t("dbProps.docMode")}</div>
            <div className="dbPropsRow">
              <span className="dbPropsLabel">{t("dbProps.ingestionType")}</span>
              <div className="dbPropsModeGroup">
                <button type="button" className={`dbPropsModeBtn${isBulk ? " active" : ""}`} onClick={() => set("documentMode", "BulkContainer")}>{t("dbProps.bulkContainer")}</button>
                <button type="button" className={`dbPropsModeBtn${!isBulk ? " active" : ""}`} onClick={() => set("documentMode", "AdHoc")}>{t("dbProps.adHoc")}</button>
              </div>
            </div>
            <div className="dbPropsRow" style={{ marginTop: 4 }}>
              <span className="dbPropsLabel" style={{ alignSelf: "flex-start", paddingTop: 4 }}>{t("dbProps.acceptedTypes")}</span>
              <div className="dbPropsCheckGroup">
                {fileTypeOptions.map(ft => (
                  <CheckChip key={ft.id} label={ft.label}
                    checked={(props.acceptedFileTypes || []).includes(ft.id)}
                    onChange={() => toggleFileType(ft.id)} />
                ))}
              </div>
            </div>
            <div className="dbPropsRow">
              <span className="dbPropsLabel">{t("dbProps.driverFileExt")}</span>
              <input type="text" className="dbPropsNumberInput" placeholder="json"
                value={props.driverFileExtension || ""}
                onChange={e => set("driverFileExtension", e.target.value.trim().replace(/^\.+/, "").toLowerCase())}
                style={{ width: 90 }} />
              <span style={{ fontSize: 12, color: "#9ca3af" }}>{t("dbProps.driverFileExtHint")}</span>
            </div>
          </div>

          {isBulk && (
            <div className="dbPropsSection">
              <div className="dbPropsSectionTitle">{t("dbProps.storageCompression")}</div>
              <div className="dbPropsRow">
                <span className="dbPropsLabel">{t("dbProps.compression")}</span>
                <div className="dbPropsLevelGroup">
                  {COMPRESS_PRESETS.map(cp => (
                    <button key={cp.id} type="button"
                      className={`dbPropsLevelBtn${activePreset === cp.id ? " active" : ""}`}
                      onClick={() => applyCompressionPreset(cp.id)}>
                      {t("dbProps.compress." + cp.id.toLowerCase())}
                      {cp.chunkSize != null && cp.id !== "None" && <span style={{ marginLeft: 4, opacity: 0.55, fontWeight: 400 }}>×{cp.chunkSize}</span>}
                    </button>
                  ))}
                </div>
                {activePreset === "Custom" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <input type="number" className="dbPropsNumberInput" style={{ width: 70 }}
                      value={props.chunkSize} min={1} max={512} step={1}
                      onChange={e => set("chunkSize", Math.max(1, parseInt(e.target.value) || 1))} />
                    <span style={{ fontSize: 11, color: "#6b7280" }}>{t("dbProps.docsPerChunk")}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="dbPropsSection">
            <div className="dbPropsSectionTitle">{t("dbProps.rendering")}</div>
            <div className="dbPropsRow">
              <span className="dbPropsLabel" style={{ alignSelf: "flex-start", paddingTop: 4 }}>{t("dbProps.allowedOutputs")}</span>
              <div className="dbPropsCheckGroup">
                {RENDER_FORMATS.map(rf => (
                  <CheckChip key={rf.id} label={rf.label}
                    checked={(props.allowedRenderFormats || []).includes(rf.id)}
                    onChange={() => toggleRenderFormat(rf.id)} />
                ))}
              </div>
            </div>
          </div>

          <div className="dbPropsSection">
            <div className="dbPropsSectionTitle">{t("dbProps.download")}</div>
            <div className="dbPropsRow">
              <span className="dbPropsLabel">{t("dbProps.bulkDownload")}</span>
              <Toggle on={props.bulkDownloadEnabled} onToggle={() => set("bulkDownloadEnabled", !props.bulkDownloadEnabled)}
                label={props.bulkDownloadEnabled ? t("dbProps.enabled") : t("dbProps.disabled")} />
            </div>
            {props.bulkDownloadEnabled && (
              <div className="dbPropsRow">
                <span className="dbPropsLabel">{t("dbProps.format")}</span>
                <div className="dbPropsLevelGroup">
                  {DOWNLOAD_FORMATS.map(fmt => (
                    <button key={fmt.id} type="button"
                      className={`dbPropsLevelBtn${props.bulkDownloadFormat === fmt.id ? " active" : ""}`}
                      onClick={() => set("bulkDownloadFormat", fmt.id)}>{t("dbProps.downloadFormat." + fmt.id.toLowerCase())}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="dbPropsSection">
            <div className="dbPropsSectionTitle">{t("dbProps.postProcessing")}</div>
            <div className="dbPropsRow">
              <span className="dbPropsLabel">{t("dbProps.flowId")}</span>
              <input type="number" className="dbPropsNumberInput" placeholder="None"
                value={props.postProcessingFlowId ?? ""} min={1}
                onChange={e => { const v = e.target.value; set("postProcessingFlowId", v === "" ? null : parseInt(v)); }} />
              <span style={{ fontSize: 12, color: "#9ca3af" }}>{t("dbProps.flowIdHint")}</span>
            </div>
          </div>

          <div className="dbPropsSection dbPropsSection-hotfolder">
            <div className="dbPropsSectionTitle">{t("dbProps.hotFolder")}</div>
            <div className="dbPropsRow dbPropsRow--retention">
              <span className="dbPropsLabel">{t("dbProps.keepInputFile")}</span>
              <Toggle
                on={props.keepOriginalInputFile}
                onToggle={() => set("keepOriginalInputFile", !props.keepOriginalInputFile)}
              />
              <span className="dbPropsRetentionState">
                {props.keepOriginalInputFile ? t("dbProps.retentionKeep") : t("dbProps.retentionDelete")}
              </span>
              <span className="dbPropsRetentionHint">{t("dbProps.keepInputFileHint")}</span>
            </div>
            <div className="dbPropsRow dbPropsRow--retention">
              <span className="dbPropsLabel">{t("dbProps.keepInputIndexFile")}</span>
              <Toggle
                on={props.keepOriginalInputIndexFile}
                onToggle={() => set("keepOriginalInputIndexFile", !props.keepOriginalInputIndexFile)}
              />
              <span className="dbPropsRetentionState">
                {props.keepOriginalInputIndexFile ? t("dbProps.retentionKeep") : t("dbProps.retentionDelete")}
              </span>
              <span className="dbPropsRetentionHint">{t("dbProps.keepInputIndexFileHint")}</span>
            </div>
            <div className="dbPropsRow">
              <span className="dbPropsLabel">{t("dbProps.dropFolders")}</span>
              <button
                type="button"
                className="dbProps-dropfolder-link"
                onClick={() => navigate("/upload", { state: { section: "drop" } })}
              >
                📂 {t("dbProps.manageDropFolders", { name: database?.dbDisplayName || database?.dbName || database?.name || t("dbProps.thisDatabase") })} →
              </button>
            </div>
          </div>

          </>)}

          {(section === "all" || section === "mapping") && (
          <div className="dbPropsSection">
            <div className="dbPropsSectionTitle">{t("dbProps.fieldMapping")}</div>
            <p className="dbMappingSectionDesc">
              {t("dbProps.mappingHint")}
            </p>

            <table className="dbMappingGrid">
              <thead>
                <tr>
                  <th className="dbMappingThCol">{t("dbProps.mappingColHeader")}</th>
                  <th className="dbMappingThExpr">{t("dbProps.mappingExprHeader")}</th>
                  <th className="dbMappingThPreview" title={t("dbProps.mappingSampleTitle")}>{t("dbProps.mappingSampleHeader")}</th>
                  <th className="dbMappingThAction"></th>
                </tr>
              </thead>
              <tbody>
                {SYSTEM_MAPPABLE_COLUMNS.map(col => (
                  <MappingTableRow key={col.name}
                    colKey={col.name} colName={col.name} colType={col.type} isSystem
                    autoDefault={col.autoDefault}
                    mapping={columnMappings[col.name] || {}}
                    sampleData={sampleData}
                    onMappingChange={m => updateColumnMapping(col.name, m)}
                    onEdit={() => setEditingCol({ colKey: col.name, colName: col.name, colType: col.type, isSystem: true })}
                  />
                ))}
                {dbFields.length > 0 && (
                  <tr><td colSpan={4} className="dbMappingGroupSep">{t("dbProps.indexFields")}</td></tr>
                )}
                {dbFields.map(ix => {
                  const name  = ix.fieldName || ix.FieldName || "";
                  const dtype = (ix.dataType || ix.DataType || "text").toLowerCase();
                  if (!name) return null;
                  return (
                    <MappingTableRow key={name}
                      colKey={name} colName={name} colType={dtype} isSystem={false}
                      mapping={columnMappings[name] || {}}
                      sampleData={sampleData}
                      onMappingChange={m => updateColumnMapping(name, m)}
                      onEdit={() => setEditingCol({ colKey: name, colName: name, colType: dtype, isSystem: false })}
                    />
                  );
                })}
              </tbody>
            </table>

            {dbFields.length === 0 && (
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>
                {t("dbProps.noIndexFields")}
              </p>
            )}
          </div>
          )}

          <div className="dbPropsFooter">
            {saveMsg && <span className="dbPropsSaveMsg">✓ {saveMsg}</span>}
            {saveErr && <span className="dbPropsSaveErr">{saveErr}</span>}
            <button type="button" className="primary-btn" disabled={saving} onClick={handleSave}>
              {saving ? t("common.saving") : t("dbProps.saveProperties")}
            </button>
          </div>
        </div>
      )}

      {editingCol && (
        <ExpressionModal
          colKey={editingCol.colKey}
          colName={editingCol.colName}
          colType={editingCol.colType}
          isSystem={editingCol.isSystem}
          mapping={columnMappings[editingCol.colKey] || {}}
          dbFields={dbFields}
          onSave={mapping => { updateColumnMapping(editingCol.colKey, mapping); setEditingCol(null); }}
          onClose={() => setEditingCol(null)}
        />
      )}

      {conversionWarn && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: "28px 32px",
            maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#1a2540" }}>{t("dbProps.conversionNotConfigured")}</span>
            </div>
            <p style={{ fontSize: 14, color: "#4b5563", marginBottom: 12 }}>
              {t("dbProps.conversionWarnMsg", { count: conversionWarn.missing.length })}
            </p>
            <ul style={{ margin: "0 0 16px 0", padding: "0 0 0 18px" }}>
              {conversionWarn.missing.map(({ input, output }) => (
                <li key={`${input}-${output}`} style={{ fontSize: 14, fontWeight: 600, color: "#dc2626", marginBottom: 4 }}>
                  {input} → {output}
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
              {t("dbProps.conversionWarnHelp", { count: conversionWarn.missing.length })}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setConversionWarn(null)}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => { setConversionWarn(null); navigate("/admin/definitions"); }}
              >
                {t("dbProps.goToDefinitions")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
